import React from "react";
import { TID } from "@/lib/testIds";
import { SectionTitle } from "@/components/FinancialCharts";
import { FileText, Sparkle } from "lucide-react";

export default function AIReport({ data }) {
  return (
    <section>
      <SectionTitle icon={FileText} title="完整 AI 研究報告" subtitle="AI Research Report" />

      <article className="card-tactical p-5 sm:p-7 lg:p-8 space-y-6" data-testid={TID.aiReport}>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-blue-500/30 bg-blue-500/10 text-blue-300 text-[10px] uppercase tracking-widest font-semibold">
            <Sparkle className="w-3 h-3" strokeWidth={2} />
            AI Generated
          </div>
          <span className="text-[10px] font-mono text-zinc-500">
            Model · Claude Sonnet 4.5 · 資料為情境展示用
          </span>
        </div>

        <ReportSection title="1. 公司摘要" body={data.summary} />

        <ReportSection
          title="2. 投資亮點"
          body={
            <ul className="space-y-2">
              {(data.highlights || []).map((h, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                  <span className="text-sm text-zinc-300 leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
          }
        />

        <ReportSection
          title="3. 財務表現分析"
          body={
            <p className="text-sm text-zinc-300 leading-relaxed">
              過去 4 季 (TTM) {data.name_zh || data.name} 營收合計約 {ttm(data.financials.revenue).toFixed(1)}B，
              AI 推估未來 4 季合計約 {fwd(data.financials.revenue).toFixed(1)}B，
              年增率約 {yoyGrowth(data).toFixed(1)}%。
              最新一季毛利率為 {lastActual(data.financials.gross_margin).toFixed(1)}%，
              自由現金流{
                lastActual(data.financials.free_cashflow) > 0
                  ? "為正，具備自我造血能力"
                  : "為負，需觀察資本支出節奏"
              }。
              整體獲利能力{data.ai_score.profitability >= 80 ? "強勁" : data.ai_score.profitability >= 60 ? "穩健" : "偏弱"}，
              財務體質{data.ai_score.safety >= 80 ? "穩固" : data.ai_score.safety >= 60 ? "尚佳" : "需持續觀察"}。
            </p>
          }
        />

        <ReportSection
          title="4. 產業與競爭分析"
          body={
            <p className="text-sm text-zinc-300 leading-relaxed">
              {data.name_zh || data.name} 位於「{data.industry}」，主要競爭對手包含
              {(data.competitors || []).map((c) => ` ${c.name}`).join("、")}。
              護城河評分 {data.ai_score.moat}／100，
              {data.ai_score.moat >= 85 ? "屬於同業中最具競爭優勢的公司" : "在同業中仍具相對優勢"}。
              長期潛力評分 {data.ai_score.long_term}／100，
              {data.ai_score.long_term >= 80 ? "受惠於結構性成長趨勢" : "需關注產業結構變化"}。
            </p>
          }
        />

        <ReportSection
          title="5. 最新新聞影響"
          body={
            <div className="space-y-2">
              {(data.news || []).slice(0, 3).map((n, i) => (
                <div key={i} className="text-sm text-zinc-300">
                  <span
                    className={`font-semibold ${
                      n.sentiment === "positive"
                        ? "text-emerald-400"
                        : n.sentiment === "negative"
                        ? "text-red-400"
                        : "text-zinc-300"
                    }`}
                  >
                    [{n.category}]
                  </span>{" "}
                  {n.title} — {n.impact}
                </div>
              ))}
            </div>
          }
        />

        <ReportSection
          title="6. 主要風險"
          body={
            <ul className="space-y-1.5">
              {[...(data.risks?.short || []), ...(data.risks?.mid || []), ...(data.risks?.long || [])]
                .slice(0, 5)
                .map((r, i) => (
                  <li key={i} className="text-sm text-zinc-300">
                    <span className="text-red-400 font-semibold">• {r.title}：</span>
                    <span className="text-zinc-400"> {r.impact}</span>
                  </li>
                ))}
            </ul>
          }
        />

        <ReportSection
          title="7. AI 綜合評分"
          body={
            <div className="text-sm text-zinc-300 leading-relaxed">
              成長性 {data.ai_score.growth}／獲利能力 {data.ai_score.profitability}／
              財務安全性 {data.ai_score.safety}／競爭優勢 {data.ai_score.moat}／
              估值合理性 {data.ai_score.valuation}／新聞情緒 {data.ai_score.sentiment}／
              長期潛力 {data.ai_score.long_term}
              <div className="mt-2">
                <span className="text-white font-semibold">整體評分：</span>
                <span className="metric-value text-lg text-blue-400 font-bold ml-1">
                  {data.ai_score.overall} / 100
                </span>
                <div className="mt-1 text-zinc-400">{data.ai_score.verdict}</div>
              </div>
            </div>
          }
        />

        <ReportSection title="8. 結論" body={data.conclusion} />

        <div className="border-t border-zinc-800 pt-4 text-[11px] text-zinc-500 leading-relaxed">
          本 AI 報告僅為研究與資訊整理參考，不構成任何投資建議。投資有風險，請自行判斷並諮詢專業人士。
        </div>
      </article>
    </section>
  );
}

function ReportSection({ title, body }) {
  return (
    <div>
      <h4 className="text-sm sm:text-base font-bold text-white mb-2 tracking-tight">
        {title}
      </h4>
      {typeof body === "string" ? (
        <p className="text-sm text-zinc-300 leading-relaxed">{body}</p>
      ) : (
        body
      )}
    </div>
  );
}

function cagr(series) {
  if (!series || series.length < 2) return 0;
  const first = series[0].value;
  const last = series[series.length - 1].value;
  if (first <= 0) return 0;
  return (Math.pow(last / first, 1 / (series.length - 1)) - 1) * 100;
}

function ttm(series) {
  return (series || []).filter((q) => !q.is_forecast).reduce((a, b) => a + (b.value || 0), 0);
}
function fwd(series) {
  return (series || []).filter((q) => q.is_forecast).reduce((a, b) => a + (b.value || 0), 0);
}
function yoyGrowth(data) {
  const t = ttm(data.financials.revenue);
  const f = fwd(data.financials.revenue);
  return t > 0 ? ((f - t) / t) * 100 : 0;
}
function lastActual(series) {
  const s = (series || []).filter((q) => !q.is_forecast);
  if (!s.length) return 0;
  const v = s[s.length - 1].value;
  return v == null ? 0 : v;
}
